import {
  INBOX_SOURCES,
  type InboxItem,
  type Person,
  type ProjectSummary,
  type TaskView,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { date, plural } from '../lib/format';
import { projectsQuery } from '../lib/projects';
import { useMe } from '../lib/session';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../ui/feedback';
import { Field, Input, Select, Textarea } from '../ui/form';
import { Page, PageHeader, Panel } from '../ui/layout';

export const inboxQuery = queryOptions({
  queryKey: ['inbox'],
  queryFn: () => api<InboxItem[]>('/inbox'),
  refetchInterval: 60_000,
});
const tasksQuery = (scope: 'mine' | 'issued') =>
  queryOptions({
    queryKey: ['tasks', scope],
    queryFn: () => api<TaskView[]>('/tasks', { query: { scope, all: scope === 'issued' } }),
  });
const peopleQuery = queryOptions({ queryKey: ['people'], queryFn: () => api<Person[]>('/people') });

function NewTaskDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const people = useQuery(peopleQuery);
  const projects = useQuery(projectsQuery('active'));
  const [f, setF] = useState({ title: '', assigneeId: '', dueDate: '', projectId: '', details: '' });
  const m = useMutation({
    mutationFn: () =>
      api<TaskView>('/tasks', {
        body: {
          title: f.title.trim(),
          assigneeId: f.assigneeId,
          dueDate: f.dueDate || null,
          projectId: f.projectId || null,
          details: f.details.trim() || null,
        },
      }),
    onSuccess: (t) => {
      for (const k of ['tasks', 'inbox']) qc.invalidateQueries({ queryKey: [k] });
      toast(`Поручение выдано: ${t.assigneeName}`);
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новое поручение"
      description="Исполнитель увидит его в «Ждёт меня»; выполнение контролирует тот, кто выдал."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={f.title.trim().length < 3 || !f.assigneeId}
            onClick={() => m.mutate()}
          >
            Выдать поручение
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Что сделать" className="sm:col-span-2">
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        </Field>
        <Field label="Исполнитель">
          <Select value={f.assigneeId} onChange={(e) => setF({ ...f, assigneeId: e.target.value })}>
            <option value="">— выберите —</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
                {p.position ? `, ${p.position}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Срок">
          <Input type="date" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} />
        </Field>
        <Field label="Проект" className="sm:col-span-2">
          <Select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value })}>
            <option value="">— без проекта —</option>
            {(projects.data ?? []).map((p: ProjectSummary) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Подробности" className="sm:col-span-2">
          <Textarea rows={3} value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} />
        </Field>
      </div>
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}

function Tasks() {
  const qc = useQueryClient();
  const [scope, setScope] = useState<'mine' | 'issued'>('mine');
  const [creating, setCreating] = useState(false);
  const q = useQuery(tasksQuery(scope));
  const act = useMutation({
    mutationFn: (v: { id: string; action: 'done' | 'cancel' | 'reopen' }) =>
      api<TaskView>(`/tasks/${v.id}/${v.action}`, { body: {} }),
    onSuccess: (t) => {
      for (const k of ['tasks', 'inbox']) qc.invalidateQueries({ queryKey: [k] });
      toast(
        t.status === 'done'
          ? 'Поручение выполнено'
          : t.status === 'cancelled'
            ? 'Поручение отменено'
            : 'Поручение снова в работе',
      );
    },
  });
  return (
    <Panel
      title="Поручения"
      bodyClassName="p-0"
      actions={
        <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
          Поручение
        </Button>
      }
    >
      <div className="border-b border-line px-5 py-3">
        <div className="inline-flex rounded-md border border-line bg-surface p-0.5">
          {(
            [
              ['mine', 'Мне'],
              ['issued', 'Выданные мной'],
            ] as const
          ).map(([k, t]) => (
            <button
              key={k}
              type="button"
              onClick={() => setScope(k)}
              className={clsx(
                'h-11 rounded-[3px] px-3 text-[13px] font-medium md:h-8',
                scope === k ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      {q.isPending ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : q.isError ? (
        <ErrorBox error={q.error} className="m-5" />
      ) : q.data.length ? (
        <ul className="divide-y divide-line">
          {q.data.map((t) => (
            <li key={t.id} className="flex flex-wrap items-start gap-3 px-5 py-3">
              <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                <p
                  className={clsx(
                    'text-[14px] font-medium',
                    t.status !== 'open' && 'text-ink-2 line-through',
                  )}
                >
                  {t.title}
                </p>
                <p className="text-[12px] text-ink-2">
                  {scope === 'mine' ? `от ${t.createdByName ?? '—'}` : `исполнитель ${t.assigneeName}`}
                  {t.projectCode ? ` · ${t.projectCode}` : ''}
                  {t.details ? ` · ${t.details}` : ''}
                </p>
                {t.linkPath ? (
                  <Link
                    to={t.linkPath}
                    className="mt-1 inline-flex min-h-11 items-center text-[13px] font-medium text-ink underline-offset-4 hover:underline md:min-h-0"
                  >
                    К записи обсуждения
                  </Link>
                ) : null}
              </div>
              <span className={clsx('num text-[13px]', t.overdue ? 'font-medium text-danger' : 'text-ink-2')}>
                {t.dueDate ? `до ${date(t.dueDate)}` : 'без срока'}
              </span>
              {t.status === 'open' ? (
                <Button
                  size="sm"
                  loading={act.isPending}
                  onClick={() => act.mutate({ id: t.id, action: 'done' })}
                >
                  Выполнено
                </Button>
              ) : (
                <Badge tone={t.status === 'done' ? 'success' : 'neutral'}>
                  {t.status === 'done' ? 'выполнено' : 'отменено'}
                </Badge>
              )}
              {scope === 'issued' && t.status === 'open' ? (
                <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: t.id, action: 'cancel' })}>
                  Отменить
                </Button>
              ) : null}
              {scope === 'issued' && t.status !== 'open' ? (
                <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: t.id, action: 'reopen' })}>
                  Вернуть в работу
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="p-5 text-[14px] text-ink-2">
          {scope === 'mine' ? 'Поручений вам нет.' : 'Вы ещё не выдавали поручений.'}
        </p>
      )}
      <ErrorBox error={act.error} className="m-4" />
      {creating ? <NewTaskDialog onClose={() => setCreating(false)} /> : null}
    </Panel>
  );
}

export function InboxPage() {
  const staff = Boolean(useMe().tenant?.staff);
  const q = useQuery(inboxQuery);
  const items = (q.data ?? []).filter((i) => i.source !== 'tasks');
  const overdue = items.filter((i) => i.overdue).length;
  return (
    <Page>
      <PageHeader
        title="Ждёт меня"
        subtitle="Согласования, проверки, оплаты и задачи из всех разделов, которые сейчас за вами"
      />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Panel
          title="Дела"
          meta={
            q.data
              ? `${plural(items.length, 'дело', 'дела', 'дел')}${overdue ? `, просрочено ${overdue}` : ''}`
              : undefined
          }
          bodyClassName="p-0"
        >
          {q.isPending ? (
            <div className="p-5">
              <Spinner />
            </div>
          ) : q.isError ? (
            <ErrorBox error={q.error} className="m-5" />
          ) : items.length ? (
            <ul className="divide-y divide-line">
              {items.map((i) => (
                <li key={i.id} className="flex flex-wrap items-start gap-3 px-5 py-3">
                  <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                    <div className="mb-0.5 flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                      <Badge tone="neutral">{INBOX_SOURCES[i.source]}</Badge>
                      {i.projectCode ? <span>{i.projectCode}</span> : null}
                      {i.since ? <span>с {date(i.since)}</span> : null}
                    </div>
                    <Link
                      to={i.actionPath}
                      className="inline-flex min-h-11 items-center text-[14px] font-medium [overflow-wrap:anywhere] text-ink hover:underline md:min-h-0"
                    >
                      {i.title}
                    </Link>
                    {i.details ? <p className="truncate text-[13px] text-ink-2">{i.details}</p> : null}
                  </div>
                  <span
                    className={clsx(
                      'num text-[13px] whitespace-nowrap',
                      i.overdue ? 'font-medium text-danger' : 'text-ink-2',
                    )}
                  >
                    {i.due ? `${i.overdue ? 'просрочено, ' : ''}до ${date(i.due)}` : ''}
                  </span>
                  <Link
                    to={i.actionPath}
                    className="inline-flex h-11 items-center rounded-md border border-line-strong bg-surface px-3 text-[13px] font-medium text-ink hover:underline md:h-8"
                  >
                    {i.actionLabel}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-5">
              <EmptyState title="Дел нет">
                Новые согласования, проверки и оплаты появятся здесь сами.
              </EmptyState>
            </div>
          )}
        </Panel>
        {staff ? <Tasks /> : null}
      </div>
    </Page>
  );
}
